"use client";

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { User, UserPlus, ChevronDown } from "lucide-react";
import { useSearchUsersGraphQL, useFetchMoreUsers } from "@/lib/hooks/api/dashboard/dashboard-graphql";
import { AppUser } from "@west-tennessee-consulting/supabase";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { cn } from "@/lib/utils";
import { getRecents, addRecent, clearRecents } from "@/components/ui/responsive-select/recents";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";

interface UserSelectProps {
  currentUserId?: string;
  onUserSelect: (userId: string) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  showUnassigned?: boolean;
  showAllUsers?: boolean;
  /**
   * @deprecated No longer gates width — `UserSelect` always fills its row now,
   * matching every other component in the select family (ClientSelectWithModal,
   * CompanySelectWithModal, TicketSelectWithModal all ignore this same prop and
   * are unconditionally full-width). Kept for backward-compatible call sites.
   */
  fullWidth?: boolean;
  className?: string;
  pageSize?: number;
  /** When true, expand inline (search + list below) instead of popover. Default true for tables/modals. Pass false for popover. */
  expandInline?: boolean;
  /** When expandInline: "floating" (default) = trigger stays, dropdown floats; "replace" = trigger replaced by panel */
  expandInlineVariant?: "replace" | "floating";
  /**
   * Optional resource identifier for filtering users by resource access
   * When provided, only users with access to this resource will be shown
   * Example: "contacts", "tickets", "time_entries"
   */
  resourceId?: string;
}

export const UserSelect: React.FC<UserSelectProps> = ({
  currentUserId,
  onUserSelect,
  disabled = false,
  placeholder = "Assign to...",
  showUnassigned = true,
  showAllUsers = false,
  fullWidth: _fullWidth = false,
  className,
  pageSize = 10,
  expandInline = true,
  expandInlineVariant = "floating",
  resourceId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserIdFromAuth, setCurrentUserIdFromAuth] = useState<string | null>(null);

  // Debounced search
  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });

  // Get current user ID from auth
  useEffect(() => {
    const getCurrentUserId = async () => {
      try {
        const supabase = getBrowserSupabaseClient();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user?.id) {
          setCurrentUserIdFromAuth(user.id);
        }
      } catch (error) {
        console.error("Error getting current user:", error);
      }
    };
    void getCurrentUserId();
  }, []);

  // GraphQL hooks for users with pagination
  const {
    users,
    pageInfo,
    isLoading: usersLoading,
    error,
    fetchMore,
  } = useSearchUsersGraphQL(
    debouncedSearchValue,
    pageSize,
    true // isActive
  );

  const fetchMoreUsers = useFetchMoreUsers();

  /**
   * Filter users by resource access if resourceId is provided
   * In a real implementation, this would check user permissions against the resource
   */
  const filteredUsers = useMemo(() => {
    if (!resourceId || !users) return users;

    // For now, return all users - in a real implementation, this would filter
    // based on user roles/permissions for the specific resource
    // Example: return users.filter(u => u.hasAccessTo(resourceId));
    return users;
  }, [users, resourceId]);

  // Build options for ResponsiveCommandSelect
  const buildOptions = useCallback(() => {
    const options: any[] = [];

    // Add "Assign to Me" option
    options.push({
      value: "me",
      label: "Assign to Me",
      description: "Assign to yourself",
      searchableText: "me assign myself",
    });

    // Add "All Users" option if enabled
    if (showAllUsers) {
      options.push({
        value: "all-users",
        label: "All Users",
        description: "Show all users",
        searchableText: "all users",
      });
    }

    // Add "Unassigned" option if enabled
    if (showUnassigned) {
      options.push({
        value: "unassigned",
        label: "Unassigned",
        description: "No assignment",
        searchableText: "unassigned none",
      });
    }

    // Add users from GraphQL (filtered by resource if provided)
    if (filteredUsers && filteredUsers.length > 0) {
      filteredUsers.forEach((user) => {
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
        options.push({
          value: user.id,
          label: fullName || user.email,
          description: fullName ? user.email : undefined,
          searchableText: [fullName, user.email].filter(Boolean).join(" "),
        });
      });
    }

    // Ensure currently selected user always has a friendly label in the trigger,
    // even if that user is not present in the currently fetched page of options.
    if (
      currentUserId &&
      currentUserId !== "all-users" &&
      currentUserId !== "unassigned" &&
      currentUserId !== "me" &&
      !options.some((option) => option.value === currentUserId)
    ) {
      const currentUser = filteredUsers?.find((u) => u.id === currentUserId);
      const fullName = currentUser
        ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim()
        : "";
      const recentLabel = getRecents("users")
        .find((recent) => recent.id === currentUserId)
        ?.label?.trim();

      options.push({
        value: currentUserId,
        label: fullName || recentLabel || "Selected User",
        description: currentUser?.email,
        searchableText: [fullName, recentLabel, currentUser?.email].filter(Boolean).join(" "),
      });
    }

    return options;
  }, [filteredUsers, showAllUsers, showUnassigned, currentUserId]);

  const options = useMemo(() => buildOptions(), [buildOptions]);

  // Get current user display name
  const getCurrentUserDisplay = () => {
    if (!currentUserId) return showAllUsers ? "All Users" : "Unassigned";

    if (currentUserId === "me" || currentUserId === currentUserIdFromAuth) return "Assign to Me";
    if (currentUserId === "unassigned") return "Unassigned";
    if (currentUserId === "all-users") return "All Users";

    const currentUser = filteredUsers?.find((u) => u.id === currentUserId);
    if (!currentUser) return "Unknown User";

    const fullName = `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim();
    return fullName || currentUser.email;
  };

  const handleUserSelect = useCallback(
    async (userId: string) => {
      if (userId === currentUserId) {
        return;
      }

      setIsLoading(true);
      try {
        const actualUserId =
          userId === "me" && currentUserIdFromAuth ? currentUserIdFromAuth : userId;
        await onUserSelect(actualUserId);

        // Record recent selections
        if (actualUserId !== "unassigned" && actualUserId !== "all-users") {
          const u = filteredUsers?.find((x) => x.id === actualUserId);
          const label = u
            ? u.first_name && u.last_name
              ? `${u.first_name} ${u.last_name}`
              : u.email
            : userId === "me"
              ? "Me"
              : actualUserId;
          try {
            addRecent("users", { id: actualUserId, label }, 8);
          } catch {}
        }
      } catch (error) {
        console.error("Error selecting user:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentUserId, currentUserIdFromAuth, onUserSelect, filteredUsers]
  );

  // Handle scroll to load more
  const handleLoadMore = useCallback(async () => {
    if (pageInfo?.hasNextPage && !usersLoading) {
      await fetchMoreUsers(fetchMore, pageInfo.endCursor, debouncedSearchValue, pageSize, true);
    }
  }, [pageInfo, usersLoading, fetchMore, fetchMoreUsers, debouncedSearchValue, pageSize]);

  const handleValueChange = useCallback(
    (userId: string) => {
      void handleUserSelect(userId);
    },
    [handleUserSelect]
  );

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchValue(val);
    },
    [setSearchValue]
  );

  return (
    <ResponsiveCommandSelect
      value={currentUserId || ""}
      onValueChange={handleValueChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled || isLoading || usersLoading}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      isSearching={usersLoading}
      className={cn("w-full", className)}
      title="Select User"
      searchPlaceholder="Search users..."
      enableRecents={true}
      recentKey="users"
      recentMax={8}
      onLoadMore={handleLoadMore}
      hasMore={!!pageInfo?.hasNextPage}
      isLoadingMore={!!users && users.length > 0 && !!usersLoading}
      expandInline={expandInline}
      expandInlineVariant={expandInlineVariant}
    />
  );
};
